/**
 * Kampagnen-Seiten:
 *   CampaignsPage    — /campaigns  (Liste, gruppiert nach Status; Wizard als Modal)
 *   CampaignDetailPage — /campaigns/:id
 */
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Drawer,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link, useNavigate } from "@tanstack/react-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { hasRole, useCurrentUser } from "../hooks/useAuth.js";
import { useEmployees } from "../hooks/useEmployees.js";
import { useSegments } from "../hooks/useSegments.js";
import {
  useCampaigns,
  useCampaign,
  useDeleteCampaign,
  useParticipations,
  useTransitionCampaign,
  useUpdateParticipation,
  useAddMembers,
  type CampaignListItem,
  type ParticipationItem,
  type TransitionCampaignPayload,
} from "../hooks/useCampaigns.js";
import { CampaignWizard } from "../components/campaigns/CampaignWizard.js";
import {
  FilterBuilder,
  emptyFilterState,
  type FilterState,
} from "../components/campaigns/FilterBuilder.js";

// ── Hilfsfunktionen ──────────────────────────────────────────────────────────

type CampaignStatus =
  | "draft"
  | "submitted"
  | "active"
  | "done"
  | "archived"
  | "rejected";

function useStatusLabel() {
  const { t } = useTranslation();
  return (status: CampaignStatus): string => t(`campaignStatus.${status}`);
}

function statusColor(status: string): string {
  const MAP: Record<string, string> = {
    draft: "gray",
    submitted: "yellow",
    active: "green",
    done: "blue",
    archived: "dark",
    rejected: "red",
  };
  return MAP[status] ?? "gray";
}

function useRelevantLabel() {
  const { t } = useTranslation();
  return (v: string): string => {
    if (v === "yes" || v === "no" || v === "not_assessed") {
      return t(`participationStatus.relevant.${v}`);
    }
    return v;
  };
}

function usePresentedLabel() {
  const { t } = useTranslation();
  return (v: string): string => {
    if (v === "yes" || v === "no" || v === "not_done" || v === "planned") {
      return t(`participationStatus.presented.${v}`);
    }
    return v;
  };
}

function useInstalledLabel() {
  const { t } = useTranslation();
  return (v: string): string => {
    if (v === "yes" || v === "no" || v === "not_done" || v === "in_progress") {
      return t(`participationStatus.installed.${v}`);
    }
    return v;
  };
}

// ── CampaignsPage ────────────────────────────────────────────────────────────

export function CampaignsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [wizardOpen, { open: openWizard, close: closeWizard }] = useDisclosure(false);

  const { data: allCampaigns, isLoading, isError } = useCampaigns({});

  const grouped = {
    active: (allCampaigns ?? []).filter((c) => c.status === "active"),
    submitted: (allCampaigns ?? []).filter((c) => c.status === "submitted"),
    draft: (allCampaigns ?? []).filter((c) => c.status === "draft"),
    done: (allCampaigns ?? []).filter((c) => c.status === "done" || c.status === "archived"),
  };

  const canCreate = hasRole(user, [
    "bereichsleiter",
    "stellvertreter",
    "projektleiter",
    "admin",
  ]);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>{t("pages.campaigns.title")}</Title>
        {canCreate && (
          <Button onClick={openWizard}>{t("pages.campaigns.newButton")}</Button>
        )}
      </Group>

      {isError && (
        <Alert color="red" title={t("common.error")}>
          {t("pages.campaigns.errorLoading")}
        </Alert>
      )}

      <Tabs defaultValue="active">
        <Tabs.List>
          <Tabs.Tab value="active">
            {t("pages.campaigns.tabActive")}{" "}
            {!isLoading && <Badge size="xs" color="green" ml={4}>{grouped.active.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="submitted">
            {t("pages.campaigns.tabSubmitted")}{" "}
            {!isLoading && <Badge size="xs" color="yellow" ml={4}>{grouped.submitted.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="draft">
            {t("pages.campaigns.tabDraft")}{" "}
            {!isLoading && <Badge size="xs" color="gray" ml={4}>{grouped.draft.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="done">
            {t("pages.campaigns.tabDone")}{" "}
            {!isLoading && <Badge size="xs" color="blue" ml={4}>{grouped.done.length}</Badge>}
          </Tabs.Tab>
        </Tabs.List>

        {(["active", "submitted", "draft", "done"] as const).map((tab) => (
          <Tabs.Panel key={tab} value={tab} pt="md">
            {isLoading ? (
              <Stack gap="sm">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} height={80} radius="sm" />
                ))}
              </Stack>
            ) : grouped[tab].length === 0 ? (
              <Text c="dimmed" ta="center" py="md">
                {t("pages.campaigns.empty")}
              </Text>
            ) : (
              <Stack gap="sm">
                {grouped[tab].map((c) => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    onClick={() =>
                      void navigate({ to: "/campaigns/$id", params: { id: c.id } })
                    }
                  />
                ))}
              </Stack>
            )}
          </Tabs.Panel>
        ))}
      </Tabs>

      <CampaignWizard
        opened={wizardOpen}
        onClose={closeWizard}
      />
    </Stack>
  );
}

// ── CampaignCard ─────────────────────────────────────────────────────────────

function CampaignCard({
  campaign,
  onClick,
}: {
  campaign: CampaignListItem;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const statusLabel = useStatusLabel();
  const { data: employees } = useEmployees();
  const { data: segments } = useSegments();
  const employeeNameById = (id: string) =>
    employees?.find((e) => e.id === id)?.fullName ?? id;
  const segmentNameById = (id: string) =>
    segments?.find((s) => s.id === id)?.name ?? id;
  const totals = campaign.summary?.totals;

  return (
    <Box
      p="md"
      style={(theme) => ({
        border: `1px solid ${theme.colors.gray[3]}`,
        borderRadius: theme.radius.sm,
        cursor: "pointer",
        "&:hover": { background: theme.colors.gray[0] },
      })}
      onClick={onClick}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="sm">
            <Text fw={600}>{campaign.title}</Text>
            <Badge color={statusColor(campaign.status)} variant="light" size="sm">
              {statusLabel(campaign.status)}
            </Badge>
          </Group>

          <Group gap={4}>
            {campaign.targetSegments.map((s) => (
              <Badge key={s.id} color="blue" variant="light" size="xs">
                {s.name ?? segmentNameById(s.id)}
              </Badge>
            ))}
          </Group>

          {campaign.responsibleId && (
            <Text size="xs" c="dimmed">
              {t("pages.campaigns.detail.responsibleLabel")} {employeeNameById(campaign.responsibleId)}
            </Text>
          )}
        </Stack>

        {totals && (
          <Group gap="md">
            <Stack gap={2} align="center">
              <Text size="lg" fw={700} c="blue">
                {totals.relevant}
              </Text>
              <Text size="xs" c="dimmed">{t("pages.campaigns.detail.funnelRelevant")}</Text>
            </Stack>
            <Stack gap={2} align="center">
              <Text size="lg" fw={700} c="yellow">
                {totals.presented}
              </Text>
              <Text size="xs" c="dimmed">{t("pages.campaigns.detail.funnelPresented")}</Text>
            </Stack>
            <Stack gap={2} align="center">
              <Text size="lg" fw={700} c="green">
                {totals.installed}
              </Text>
              <Text size="xs" c="dimmed">{t("pages.campaigns.detail.funnelInstalled")}</Text>
            </Stack>
          </Group>
        )}
      </Group>
    </Box>
  );
}

// ── CampaignDetailPage ───────────────────────────────────────────────────────

export function CampaignDetailPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const statusLabel = useStatusLabel();
  const { user } = useCurrentUser();
  const { data: campaign, isLoading, isError } = useCampaign(id);
  const { data: participationsData, isLoading: partLoading } = useParticipations(id, { page: 1 });
  const transitionMutation = useTransitionCampaign(id);
  const addMembersMutation = useAddMembers(id);
  const deleteMutation = useDeleteCampaign();
  const navigate = useNavigate();
  const { data: employees } = useEmployees();
  const { data: segments } = useSegments();
  const employeeNameById = (employeeId: string) =>
    employees?.find((e) => e.id === employeeId)?.fullName ?? employeeId;
  const segmentNameById = (segmentId: string) =>
    segments?.find((s) => s.id === segmentId)?.name ?? segmentId;

  const [rejectModalOpen, { open: openRejectModal, close: closeRejectModal }] = useDisclosure(false);
  const [rejectReason, setRejectReason] = useState("");
  const [filterDrawerOpen, { open: openFilterDrawer, close: closeFilterDrawer }] =
    useDisclosure(false);
  const [filterState, setFilterState] = useState<FilterState>(emptyFilterState());
  const [transitionError, setTransitionError] = useState<string | null>(null);

  if (isError) {
    return (
      <Alert color="red" title={t("common.error")}>
        {t("pages.campaigns.detail.errorLoadingSingle")}
      </Alert>
    );
  }

  const isBL = hasRole(user, ["bereichsleiter", "stellvertreter"]);
  const isAdmin = hasRole(user, "admin");

  async function handleDelete() {
    if (!campaign) return;
    const confirmed = window.confirm(
      t("pages.campaigns.detail.deleteConfirm", { title: campaign.title }),
    );
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(id);
      await navigate({ to: "/campaigns" });
    } catch (err) {
      setTransitionError(err instanceof Error ? err.message : t("pages.campaigns.detail.errorDelete"));
    }
  }

  async function handleTransition(to: TransitionCampaignPayload["to"], reason?: string) {
    setTransitionError(null);
    try {
      await transitionMutation.mutateAsync({ to, reason });
    } catch (err) {
      setTransitionError(err instanceof Error ? err.message : t("pages.campaigns.detail.errorTransition"));
    }
  }

  function renderWorkflowActions() {
    if (!campaign) return null;

    const actions: React.ReactNode[] = [];
    const isInitiator = campaign.initiatorId === user?.employeeId;

    if (campaign.status === "draft" && (isInitiator || isAdmin)) {
      actions.push(
        <Button
          key="submit"
          variant="light"
          color="yellow"
          size="sm"
          loading={transitionMutation.isPending}
          onClick={() => void handleTransition("submitted")}
        >
          {t("pages.campaigns.detail.actions.submit")}
        </Button>,
      );
      if (isBL || isAdmin) {
        actions.push(
          <Button
            key="self-approve"
            color="green"
            size="sm"
            loading={transitionMutation.isPending}
            onClick={() => void handleTransition("active")}
          >
            {t("pages.campaigns.detail.selfApproveLong")}
          </Button>,
        );
      }
    }

    if (campaign.status === "submitted" && isBL) {
      actions.push(
        <Button
          key="approve"
          color="green"
          size="sm"
          loading={transitionMutation.isPending}
          onClick={() => void handleTransition("active")}
        >
          {t("pages.campaigns.detail.actions.approve")}
        </Button>,
      );
      actions.push(
        <Button
          key="reject"
          color="red"
          variant="light"
          size="sm"
          onClick={openRejectModal}
        >
          {t("pages.campaigns.detail.actions.reject")}
        </Button>,
      );
    }

    if (campaign.status === "active" && (isBL || isAdmin)) {
      actions.push(
        <Button
          key="done"
          variant="light"
          color="blue"
          size="sm"
          loading={transitionMutation.isPending}
          onClick={() => void handleTransition("done")}
        >
          {t("pages.campaigns.detail.actions.complete")}
        </Button>,
      );
    }

    if (campaign.status === "done" && (isBL || isAdmin)) {
      actions.push(
        <Button
          key="archive"
          variant="light"
          color="dark"
          size="sm"
          loading={transitionMutation.isPending}
          onClick={() => void handleTransition("archived")}
        >
          {t("pages.campaigns.detail.actions.archive")}
        </Button>,
      );
    }

    // Delete: erlaubt für draft (Initiator/BL/Admin) oder archived (BL/Admin).
    // Backend prüft die Rollen final.
    const canDelete =
      (campaign.status === "draft" || campaign.status === "archived") &&
      (isBL || isAdmin || campaign.initiatorId === user?.employeeId);
    if (canDelete) {
      actions.push(
        <Button
          key="delete"
          variant="light"
          color="red"
          size="sm"
          loading={deleteMutation.isPending}
          onClick={handleDelete}
        >
          {t("pages.campaigns.detail.actions.delete")}
        </Button>,
      );
    }

    return actions.length > 0 ? (
      <Group gap="sm">{actions}</Group>
    ) : null;
  }

  const totals = campaign?.summary?.totals;

  return (
    <Stack gap="md">
      {/* Rückweg zur Liste — Detailseiten sollen keine Sackgassen sein. */}
      <Anchor component={Link} to="/campaigns" size="sm" c="dimmed">
        ← {t("pages.campaigns.title")}
      </Anchor>
      {isLoading ? (
        <>
          <Skeleton height={32} width={320} />
          <Skeleton height={20} width={200} />
        </>
      ) : (
        <>
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="sm" align="center">
                <Title order={2}>{campaign?.title}</Title>
                {campaign && (
                  <Badge color={statusColor(campaign.status)} variant="filled">
                    {statusLabel(campaign.status)}
                  </Badge>
                )}
              </Group>
              <Group gap={4}>
                {campaign?.targetSegments.map((s) => (
                  <Badge key={s.id} color="blue" variant="light" size="sm">
                    {s.name ?? segmentNameById(s.id)}
                  </Badge>
                ))}
              </Group>
              {campaign?.responsibleId && (
                <Text size="sm" c="dimmed">
                  {t("pages.campaigns.detail.responsibleLabel")} {employeeNameById(campaign.responsibleId)}
                </Text>
              )}
            </Stack>

            {/* Trichter — Gesamt + Aufschlüsselung pro Zielsegment (T10). */}
            {totals && (
              <Stack gap="xs" align="flex-end">
                <Group gap="xl">
                  <Stack gap={2} align="center">
                    <Text size="xl" fw={700} c="blue">
                      {totals.relevant}
                    </Text>
                    <Text size="xs" c="dimmed">{t("pages.campaigns.detail.funnelRelevant")}</Text>
                  </Stack>
                  <Text c="dimmed">→</Text>
                  <Stack gap={2} align="center">
                    <Text size="xl" fw={700} c="yellow">
                      {totals.presented}
                    </Text>
                    <Text size="xs" c="dimmed">{t("pages.campaigns.detail.funnelPresented")}</Text>
                  </Stack>
                  <Text c="dimmed">→</Text>
                  <Stack gap={2} align="center">
                    <Text size="xl" fw={700} c="green">
                      {totals.installed}
                    </Text>
                    <Text size="xs" c="dimmed">{t("pages.campaigns.detail.funnelInstalled")}</Text>
                  </Stack>
                  <Stack gap={2} align="center">
                    <Text size="xl" fw={700} c="gray">
                      {totals.total}
                    </Text>
                    <Text size="xs" c="dimmed">{t("pages.campaigns.detail.funnelTotal")}</Text>
                  </Stack>
                </Group>
                {(campaign?.summary?.perSegment?.length ?? 0) > 1 && (
                  <Group gap="sm">
                    {campaign!.summary!.perSegment!.map((p) => (
                      <Badge key={p.segmentId} size="sm" variant="light" color="gray">
                        {p.segmentName}: {p.totals.relevant}/{p.totals.presented}/{p.totals.installed} ({p.totals.total})
                      </Badge>
                    ))}
                  </Group>
                )}
              </Stack>
            )}
          </Group>

          {/* Workflow-Aktionen */}
          {renderWorkflowActions()}
          {transitionError && (
            <Alert color="red" title={t("pages.campaigns.detail.statusErrorTitle")}>
              {transitionError}
            </Alert>
          )}

          {/* Mitglieder-Tabelle */}
          <Group justify="space-between">
            <Title order={4}>{t("pages.campaigns.detail.membersTitle")}</Title>
            <Group gap="xs">
              {campaign?.status === "active" && (
                <Button
                  size="sm"
                  variant="subtle"
                  onClick={() =>
                    void navigate({
                      to: "/campaigns/$id/members",
                      params: { id },
                    })
                  }
                >
                  {t("pages.campaigns.detail.bulkOpenButton")}
                </Button>
              )}
              {(isBL || isAdmin) && campaign?.status === "active" && (
                <Button size="sm" variant="light" onClick={openFilterDrawer}>
                  {t("pages.campaigns.detail.addMembersButton")}
                </Button>
              )}
            </Group>
          </Group>

          {partLoading ? (
            <Stack gap="xs">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={40} />
              ))}
            </Stack>
          ) : (participationsData?.items.length ?? 0) === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              {t("pages.campaigns.detail.noMembers")}
            </Text>
          ) : (
            <ParticipationsMatrix
              campaignId={id}
              items={participationsData?.items ?? []}
              targetSegments={(campaign?.targetSegments ?? []).map((s) => ({
                id: s.id,
                name: s.name ?? segmentNameById(s.id),
              }))}
            />
          )}
        </>
      )}

      {/* Ablehnen-Modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={closeRejectModal}
        title={t("pages.campaigns.detail.rejectModalTitle")}
      >
        <Stack gap="md">
          <Textarea
            label={t("pages.campaigns.detail.rejectReasonLabel")}
            placeholder={t("pages.campaigns.detail.rejectReasonPlaceholderLong")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeRejectModal}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              disabled={!rejectReason.trim()}
              loading={transitionMutation.isPending}
              onClick={() => {
                void handleTransition("rejected", rejectReason.trim());
                closeRejectModal();
              }}
            >
              {t("pages.campaigns.detail.rejectConfirm")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Mitglieder hinzufügen Drawer */}
      <Drawer
        opened={filterDrawerOpen}
        onClose={closeFilterDrawer}
        title={t("pages.campaigns.detail.addMembersDrawerTitle")}
        position="right"
        size="lg"
      >
        <Stack gap="md" p="xs">
          <FilterBuilder
            value={filterState}
            onChange={setFilterState}
            campaignId={id}
            showPreview
            onAddCustomers={(picks, addType) => {
              void addMembersMutation.mutateAsync({
                customers: picks.map((p) => ({ customerId: p.id, addType })),
              });
            }}
          />
          {addMembersMutation.isError && (
            <Alert color="red" title={t("common.error")}>
              {t("pages.campaigns.detail.addMembersError")}
              {addMembersMutation.error instanceof Error &&
              addMembersMutation.error.message
                ? ` (${addMembersMutation.error.message})`
                : ""}
            </Alert>
          )}
        </Stack>
      </Drawer>
    </Stack>
  );
}

// ── 2D-Matrix: Kunden × Zielsegmente (T10) ───────────────────────────────────
//
// Jedes (Kunde, Segment)-Paar ist eine eigene Teilnahme. Pro Zelle drei
// Selects (Relevant/Vorgestellt/Eingerichtet). Hat eine Kampagne nur ein
// Zielsegment (oder alle Kunden sind single-segment), kollabiert die Matrix
// optisch auf eine Spalte und wirkt wie die bisherige Liste.

interface ParticipationMatrixProps {
  campaignId: string;
  items: ParticipationItem[];
  targetSegments: Array<{ id: string; name: string }>;
}

function ParticipationsMatrix({
  campaignId,
  items,
  targetSegments,
}: ParticipationMatrixProps) {
  const { t } = useTranslation();
  // Build customer → (segmentId → ParticipationItem) lookup.
  const byCustomer = new Map<
    string,
    {
      customerId: string;
      customerName: string;
      cells: Map<string, ParticipationItem>;
    }
  >();
  for (const it of items) {
    const entry = byCustomer.get(it.customerId) ?? {
      customerId: it.customerId,
      customerName: it.customerName,
      cells: new Map<string, ParticipationItem>(),
    };
    entry.cells.set(it.segmentId, it);
    byCustomer.set(it.customerId, entry);
  }
  const customers = [...byCustomer.values()].sort((a, b) =>
    a.customerName.localeCompare(b.customerName, "de"),
  );

  return (
    <Box style={{ overflowX: "auto" }}>
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th rowSpan={2}>{t("pages.campaigns.detail.tableCustomer")}</Table.Th>
            <Table.Th rowSpan={2}>{t("pages.campaigns.detail.tableVia")}</Table.Th>
            {targetSegments.map((s) => (
              <Table.Th key={s.id} colSpan={3} ta="center">
                {s.name}
              </Table.Th>
            ))}
          </Table.Tr>
          <Table.Tr>
            {targetSegments.map((s) => (
              <React.Fragment key={`${s.id}-headers`}>
                <Table.Th>{t("pages.campaigns.detail.tableRelevant")}</Table.Th>
                <Table.Th>{t("pages.campaigns.detail.tablePresented")}</Table.Th>
                <Table.Th>{t("pages.campaigns.detail.tableInstalled")}</Table.Th>
              </React.Fragment>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {customers.map((c) => {
            // "Via" = addType der ersten gefüllten Zelle (alle gleich, weil
            // beim Hinzufügen pro Kunde gemeinsamer addType verwendet wird).
            const firstCell = [...c.cells.values()][0];
            return (
              <Table.Tr key={c.customerId}>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {c.customerName}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {firstCell && (
                    <Badge
                      size="xs"
                      color={firstCell.addType === "filter" ? "blue" : "gray"}
                      variant="light"
                    >
                      {firstCell.addType === "filter"
                        ? t("pages.campaigns.detail.viaFilterLabel")
                        : t("pages.campaigns.detail.viaManualLabel")}
                    </Badge>
                  )}
                </Table.Td>
                {targetSegments.map((s) => {
                  const cell = c.cells.get(s.id);
                  if (!cell) {
                    return (
                      <Table.Td key={s.id} colSpan={3}>
                        <Text size="xs" c="dimmed" ta="center">
                          —
                        </Text>
                      </Table.Td>
                    );
                  }
                  return (
                    <ParticipationCells
                      key={s.id}
                      campaignId={campaignId}
                      item={cell}
                    />
                  );
                })}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

function ParticipationCells({
  campaignId,
  item,
}: {
  campaignId: string;
  item: ParticipationItem;
}) {
  const relevantLabel = useRelevantLabel();
  const presentedLabel = usePresentedLabel();
  const installedLabel = useInstalledLabel();
  const updateMutation = useUpdateParticipation(campaignId, item.id);

  function update(patch: Parameters<typeof updateMutation.mutate>[0]) {
    updateMutation.mutate(patch);
  }

  return (
    <>
      <Table.Td>
        <Select
          size="xs"
          data={[
            { value: "not_assessed", label: relevantLabel("not_assessed") },
            { value: "yes", label: relevantLabel("yes") },
            { value: "no", label: relevantLabel("no") },
          ]}
          value={item.relevant}
          onChange={(v) => {
            if (v) update({ relevant: v as ParticipationItem["relevant"] });
          }}
          style={{ minWidth: 130 }}
        />
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          data={[
            { value: "not_done", label: presentedLabel("not_done") },
            { value: "planned", label: presentedLabel("planned") },
            { value: "yes", label: presentedLabel("yes") },
            { value: "no", label: presentedLabel("no") },
          ]}
          value={item.presented}
          onChange={(v) => {
            if (v) update({ presented: v as ParticipationItem["presented"] });
          }}
          style={{ minWidth: 130 }}
          disabled={item.relevant === "no"}
        />
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          data={[
            { value: "not_done", label: installedLabel("not_done") },
            { value: "in_progress", label: installedLabel("in_progress") },
            { value: "yes", label: installedLabel("yes") },
            { value: "no", label: installedLabel("no") },
          ]}
          value={item.installed}
          onChange={(v) => {
            if (v) update({ installed: v as ParticipationItem["installed"] });
          }}
          style={{ minWidth: 130 }}
          disabled={item.relevant === "no" || item.presented !== "yes"}
        />
      </Table.Td>
    </>
  );
}
